import { RouterProvider } from "react-router/dom";

// Api
import GlobalTimer from "./services/timer";

// Routes
import router from "./router";

// Services
import { configureApi } from "src/services/api";

// Recorder instance
import createRecorderInstance from "./services/recorder";
import { createMediaManager } from "./services/media-manager";
import { LaunchDarklyService } from "./services/launch-darkly";
import createDataLayer from "./services/data-layer";
import { useAppSelector } from "./store/hooks";
import { useEffect, useRef, useState } from "react";
import ScreenLoader from "./components/screen-loader";
import { createFefeInstance } from "./store/call/actions";
import posthog from "./services/posthog";
import { clearCallContext } from "./services/logger";

declare global {
  interface Window {
    LOCAL: boolean;
  }
}

function App() {
  const isLocal = window?.LOCAL;
  const {
    userInfo: { id, email, name },
    isLoggedIn,
  } = useAppSelector((state) => state.auth);
  const identifiedUserId = useRef<string | null>(null);

  const [launchDarklyLoading, setLoading] = useState(isLoggedIn);

  const { VITE_GTM_ID } = import.meta.env;

  useEffect(() => {
    // Configure API
    configureApi();
    GlobalTimer();
    createRecorderInstance();
    createMediaManager();

    window.addEventListener("keydown", (event) => {
      const isReloadKey = event.key === "F5" || ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r");

      if (isReloadKey) {
        event.preventDefault();
        event.stopPropagation();

        console.log("Reload bloqueado");
      }
    });

    if (!isLocal) createDataLayer(VITE_GTM_ID);
  }, []);

  useEffect(() => {
    if (!isLoggedIn || !id) {
      if (identifiedUserId.current) {
        posthog.reset();
        clearCallContext();
        identifiedUserId.current = null;
      }
      return;
    }

    if (identifiedUserId.current === id) return;

    if (identifiedUserId.current) posthog.reset();

    posthog.identify(id, { email, name });
    identifiedUserId.current = id;
  }, [email, id, isLoggedIn, name]);

  useEffect(() => {
    if (!email) return;

    async function bootstrap() {
      const ldService = LaunchDarklyService.getInstance();
      await ldService.init({
        kind: "user",
        key: email,
      });

      setLoading(false);

      LaunchDarklyService.applyLogLevelFlag();

      const useEnfdpoint2 = await LaunchDarklyService.isFlagEnabled("should-use-fefe-alternative-endpoint", false);
      const baseURL = useEnfdpoint2 ? import.meta.env.VITE_FEFE_API_URL2 : import.meta.env.VITE_FEFE_API_URL;

      createFefeInstance(baseURL);
    }

    bootstrap();
  }, [email]);

  return launchDarklyLoading ? <ScreenLoader options={{ color: "blue", fullScreen: true }} /> : <RouterProvider router={router} />;
}

export default App;
